import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { creatorService } from '../services/creatorService';
import { usersService } from '../services/usersService';
import { postsService } from '../services/postsService';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import ShareButtons from '../components/social/ShareButtons';
import { MagnifyingGlassIcon, UserCircleIcon, DocumentTextIcon, HeartIcon, CalendarIcon } from '@heroicons/react/24/outline';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const SearchPage: React.FC = () => {
  const q = useQuery();
  const navigate = useNavigate();
  const [query, setQuery] = useState(q.get('q') || '');
  const [activeTab, setActiveTab] = useState<'creators' | 'posts' | 'users'>('creators');
  // Filters
  const [category, setCategory] = useState<string>('');
  const [minPatrons, setMinPatrons] = useState<string>('');
  const [maxPatrons, setMaxPatrons] = useState<string>('');
  const [creatorSort, setCreatorSort] = useState<'popular' | 'newest' | 'az'>('popular');
  const [postSortBy, setPostSortBy] = useState<'createdAt' | 'publishedAt' | 'likesCount'>('createdAt');
  const [postSortOrder, setPostSortOrder] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(false);
  const [creators, setCreators] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const runSearch = async (term: string) => {
    if (!term.trim()) return;
    setLoading(true);
    try {
      const [creRes, postRes, userRes] = await Promise.all([
        creatorService.searchCreators({
          query: term,
          limit: 12,
          sort: creatorSort,
          category: category || undefined,
          minPatrons: minPatrons ? Number(minPatrons) : undefined,
          maxPatrons: maxPatrons ? Number(maxPatrons) : undefined,
        }),
        postsService.list({ query: term, limit: 12, sortBy: postSortBy, sortOrder: postSortOrder } as any),
        usersService.searchAll(term, { limit: 12, userType: 'all' }),
      ]);
      setCreators(creRes.creators || []);
      setPosts(postRes.posts || []);
      setUsers(userRes.users || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = q.get('q') || '';
    setQuery(initial);
    if (initial) runSearch(initial);
  }, [q]);

  // Re-run when filters change (creators tab and posts tab only)
  useEffect(() => {
    const term = q.get('q') || '';
    if (!term) return;
    if (activeTab === 'creators' || activeTab === 'posts') {
      runSearch(term);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, minPatrons, maxPatrons, creatorSort, postSortBy, postSortOrder, activeTab]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const term = query.trim();
    if (!term) return;
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Helmet>
        <title>Search - Patreon Clone</title>
        <meta name="description" content="Search creators, posts, and users" />
      </Helmet>

      {/* Search Bar */}
      <form onSubmit={onSubmit} className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search creators, posts, users"
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--patreon-orange)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = ''; }}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Button type="submit" size="sm">Search</Button>
          </div>
        </div>
      </form>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="col-span-1">
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Category (e.g. Art)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
          />
        </div>
        <div className="flex space-x-2">
          <input
            value={minPatrons}
            onChange={(e) => setMinPatrons(e.target.value)}
            placeholder="Min patrons"
            inputMode="numeric"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
          />
          <input
            value={maxPatrons}
            onChange={(e) => setMaxPatrons(e.target.value)}
            placeholder="Max patrons"
            inputMode="numeric"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
          />
        </div>
        <div>
          <select
            value={creatorSort}
            onChange={(e) => setCreatorSort(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
          >
            <option value="popular">Sort creators: Popular</option>
            <option value="newest">Newest</option>
            <option value="az">A → Z</option>
          </select>
        </div>
        <div className="flex space-x-2">
          <select
            value={postSortBy}
            onChange={(e) => setPostSortBy(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
          >
            <option value="createdAt">Sort posts: Created</option>
            <option value="publishedAt">Published</option>
            <option value="likesCount">Likes</option>
          </select>
          <select
            value={postSortOrder}
            onChange={(e) => setPostSortOrder(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-6 border-b border-gray-200 mb-6">
        {(['creators','posts','users'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === tab
                ? 'text-[color:var(--patreon-orange)]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            style={activeTab === tab ? { borderBottomColor: 'var(--patreon-orange)' } : undefined}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          {activeTab === 'creators' && (
            creators.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-600">No creators found.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {creators.map((c: any) => (
                  <Card key={c.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4 mb-4">
                        {c.avatar ? (
                          <img src={c.avatar} alt={c.displayName || c.username} className="w-16 h-16 rounded-full object-cover" />
                        ) : (
                          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-r from-orange-400 to-pink-500 text-white font-bold text-xl">
                            {(c.displayName || c.username).charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 flex items-center">
                            {c.displayName || c.username}
                            {c.isVerified && (
                              <svg className="w-4 h-4 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">{c.category || 'Creator'}</div>
                          {c._count && (
                            <div className="text-xs text-gray-500 mt-1">
                              {c._count.memberships || 0} patrons
                            </div>
                          )}
                        </div>
                      </div>
                      {c.description && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{c.description}</p>
                      )}
                      <div className="flex items-center space-x-2">
                        <Button 
                          className="flex-1" 
                          onClick={() => navigate(`/creator/${c.username}`)}
                          size="sm"
                        >
                          View Page
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          )}

          {activeTab === 'posts' && (
            posts.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-600">No posts found.</CardContent></Card>
            ) : (
              <div className="space-y-4">
                {posts.map((p: any) => (
                  <Card key={p.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <DocumentTextIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 mb-1">{p.title || 'Untitled Post'}</h3>
                              {p.creator && (
                                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                                  {p.creator.avatar ? (
                                    <img src={p.creator.avatar} alt={p.creator.username} className="w-5 h-5 rounded-full" />
                                  ) : (
                                    <UserCircleIcon className="w-5 h-5" />
                                  )}
                                  <span>{p.creator.username}</span>
                                </div>
                              )}
                              <p className="text-sm text-gray-600 line-clamp-3 mb-3">{p.content}</p>
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                {p.publishedAt && (
                                  <span className="flex items-center">
                                    <CalendarIcon className="w-4 h-4 mr-1" />
                                    {new Date(p.publishedAt).toLocaleDateString()}
                                  </span>
                                )}
                                {p._count?.likes !== undefined && (
                                  <span className="flex items-center">
                                    <HeartIcon className="w-4 h-4 mr-1" />
                                    {p._count.likes} likes
                                  </span>
                                )}
                                {p._count?.comments !== undefined && (
                                  <span>{p._count.comments} comments</span>
                                )}
                              </div>
                            </div>
                            <ShareButtons
                              url={`${window.location.origin}/post/${p.id}`}
                              title={p.title || 'Post'}
                              description={p.content?.substring(0, 100)}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          )}

          {activeTab === 'users' && (
            users.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-600">No users found.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {users.map((u: any) => (
                  <Card key={u.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4">
                        {u.avatar ? (
                          <img src={u.avatar} alt={u.username} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <UserCircleIcon className="w-12 h-12 text-gray-400" />
                        )}
                        <div>
                          <div className="font-semibold text-gray-900">{u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}</div>
                          <div className="text-sm text-gray-600">{u.bio || 'Member'}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;


